import { fmtDate } from '../lib/format';
import React, { useState, useMemo } from 'react';
import { Bell, Send, Mail, MailOpen } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { useFetch, Spinner } from '../lib/useFetch';
import { comunicazioniApi } from '../lib/api';

const typeStyle = {
  normativa: { bg: '#e0e7ff', color: '#023F80', label: 'Normativa' },
  scadenza: { bg: '#fee2e2', color: '#b91c1c', label: 'Scadenza' },
  sistema: { bg: '#dcfce7', color: '#1e40af', label: 'Sistema' },
  evento: { bg: '#fef7d4', color: '#8a6608', label: 'Evento' },
};

const ComunicazioniPage = () => {
  const { data: messages, loading, reload } = useFetch(() => comunicazioniApi.list(), []);
  const [selected, setSelected] = useState(null);
  const [newSubject, setNewSubject] = useState('');
  const [newBody, setNewBody] = useState('');
  const [filter, setFilter] = useState('all');

  const filtered = useMemo(() => {
    const list = messages || [];
    if (filter === 'unread') return list.filter((m) => !m.letto);
    return list;
  }, [filter, messages]);

  const current = (messages || []).find((m) => m.id === selected) || (messages || [])[0];

  const handleOpen = async (id) => {
    setSelected(id);
    try {
      await comunicazioniApi.markRead(id);
      reload();
    } catch (e) { /* silent */ }
  };

  const handleSend = async () => {
    if (!newSubject.trim()) { toast.error('Inserisci un oggetto'); return; }
    try {
      await comunicazioniApi.create({ titolo: newSubject, contenuto: newBody, tipo: 'sistema' });
      setNewSubject('');
      setNewBody('');
      toast.success('Comunicazione inviata');
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore invio');
    }
  };

  if (loading) return <Spinner />;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-[#e0e7ff] text-[#023F80] flex items-center justify-center"><Bell size={20} /></div>
          <h2 className="text-lg font-bold text-gray-800">Posta in arrivo</h2>
        </div>
        <div className="flex gap-2 mb-3">
          <button onClick={() => setFilter('all')} className={`px-3 py-1 rounded-full text-xs font-semibold ${filter === 'all' ? 'bg-[#023F80] text-white' : 'bg-gray-100 text-gray-600'}`}>Tutti</button>
          <button onClick={() => setFilter('unread')} className={`px-3 py-1 rounded-full text-xs font-semibold ${filter === 'unread' ? 'bg-[#023F80] text-white' : 'bg-gray-100 text-gray-600'}`}>Non letti</button>
        </div>
        <div className="space-y-1.5 max-h-[520px] overflow-y-auto scrollbar-thin">
          {filtered.map((m) => {
            const t = typeStyle[m.tipo] || typeStyle.sistema;
            const active = (current?.id) === m.id;
            return (
              <button key={m.id} onClick={() => handleOpen(m.id)} className={`w-full text-left p-3 rounded-xl transition-colors ${active ? 'bg-[#e0e7ff]' : 'bg-gray-50 hover:bg-gray-100'}`}>
                <div className="flex items-start gap-2">
                  {m.letto ? <MailOpen size={14} className="text-gray-400 mt-0.5 shrink-0" /> : <Mail size={14} className="text-[#023F80] mt-0.5 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm truncate ${m.letto ? 'text-gray-700' : 'font-bold text-gray-900'}`}>{m.titolo}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: t.bg, color: t.color }}>{t.label}</span>
                      <span className="text-[10px] text-gray-500">{fmtDate(m.data)}</span>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
          {filtered.length === 0 && <div className="text-sm text-gray-400 text-center py-6">Nessun messaggio</div>}
        </div>
      </div>

      <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-5">
        {current ? (
          <div className="pb-5 border-b border-gray-100">
            <div className="flex items-start justify-between gap-3 mb-2">
              <h3 className="text-xl font-bold text-gray-800">{current.titolo}</h3>
              <span className="text-xs text-gray-500">{fmtDate(current.data)}</span>
            </div>
            <span className="inline-block text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: typeStyle[current.tipo]?.bg, color: typeStyle[current.tipo]?.color }}>{typeStyle[current.tipo]?.label}</span>
            <p className="mt-4 text-sm text-gray-700 leading-relaxed">{current.contenuto}</p>
          </div>
        ) : (
          <div className="text-sm text-gray-400 text-center py-10">Seleziona una comunicazione</div>
        )}

        <div>
          <h4 className="font-bold text-gray-800 mb-3 text-sm">Nuova comunicazione</h4>
          <Input value={newSubject} onChange={(e) => setNewSubject(e.target.value)} placeholder="Oggetto..." className="mb-2 border-gray-200" />
          <Textarea value={newBody} onChange={(e) => setNewBody(e.target.value)} placeholder="Scrivi il messaggio..." rows={4} className="border-gray-200 resize-none" />
          <div className="flex justify-end mt-3">
            <button onClick={handleSend} className="ro-hide px-5 py-2 rounded-full bg-[#023F80] hover:bg-[#012C5B] text-white text-xs font-bold tracking-wider flex items-center gap-2"><Send size={14} /> INVIA</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComunicazioniPage;
